import { React, useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { Grid, Typography, useTheme, useMediaQuery, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from 'react-i18next';
import CopyButton from '../common/CopyButton';
import {
  CalculatorContainer,
  CalculatorTitle,
  DateContainer,
  ResultTypography,
  datePickerStyles
} from '../../shared/styles';

// 2024년 공휴일 목록 (예시)
const HOLIDAYS_2024 = [
  '2024-01-01', // 신정
  '2024-02-09', // 설날
  '2024-02-10', // 설날
  '2024-02-11', // 설날
  '2024-02-12', // 대체공휴일
  '2024-03-01', // 삼일절
  '2024-04-10', // 국회의원선거일
  '2024-05-05', // 어린이날
  '2024-05-06', // 대체공휴일
  '2024-05-15', // 부처님오신날
  '2024-06-06', // 현충일
  '2024-08-15', // 광복절
  '2024-09-16', // 추석
  '2024-09-17', // 추석
  '2024-09-18', // 추석
  '2024-10-03', // 개천절
  '2024-10-09', // 한글날
  '2024-12-25', // 크리스마스
];

function BusinessDayCalculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [excludeHolidays, setExcludeHolidays] = useState(true);
  const [businessDays, setBusinessDays] = useState(0);

  const isWeekend = (date) => {
    const day = date.day();
    return day === 0 || day === 6; // 0: 일요일, 6: 토요일
  };

  const isHoliday = (date) => {
    return HOLIDAYS_2024.includes(date.format('YYYY-MM-DD'));
  };

  const calculateBusinessDays = useCallback(() => {
    let days = 0;
    let currentDate = startDate;

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      if (
        (!excludeWeekends || !isWeekend(currentDate)) &&
        (!excludeHolidays || !isHoliday(currentDate))
      ) {
        days++;
      }
      currentDate = currentDate.add(1, 'day');
    }

    return days;
  }, [startDate, endDate, excludeWeekends, excludeHolidays]);

  useEffect(() => {
    setBusinessDays(calculateBusinessDays());
  }, [calculateBusinessDays]);

  const resultText = `${businessDays} ${t('businessDayCalc.businessDays')}`;

  return (
    <CalculatorContainer>
      <CalculatorTitle variant={isMobile ? "h5" : "h4"}>
        {t('businessDayCalc.title')}
      </CalculatorTitle>
      <Grid
        container
        spacing={isMobile ? 2 : 3}
        justifyContent="center"
        sx={{ flexGrow: 1 }}
      >
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              {t('businessDayCalc.startDate')}
            </Typography>
            <DatePicker
              onChange={setStartDate}
              value={startDate}
              sx={datePickerStyles}
            />
          </DateContainer>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              {t('businessDayCalc.endDate')}
            </Typography>
            <DatePicker
              onChange={setEndDate}
              value={endDate}
              sx={datePickerStyles}
            />
          </DateContainer>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeWeekends}
                    onChange={(e) => setExcludeWeekends(e.target.checked)}
                    color="primary"
                  />
                }
                label="Exclude Weekends"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeHolidays}
                    onChange={(e) => setExcludeHolidays(e.target.checked)}
                    color="primary"
                  />
                }
                label="Exclude Holidays"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ResultTypography
        variant={isMobile ? "h4" : "h3"}
        sx={{ mt: 'auto', mb: 2 }}
      >
        {resultText}
        <CopyButton text={resultText} />
      </ResultTypography>
    </CalculatorContainer>
  );
}

export default BusinessDayCalculator; 